import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
  type UIEvent,
} from 'react';
import type { ExportColumn } from './useExportColumns';
import type { ExportStatus, PipingSystem } from '../types';
import type {
  TableColumnFilter,
  TableFilterOperator,
} from '../../../shared/ui/table/hooks/useTableFilters';
import {
  isFilterActive,
  matchesCondition,
  normalizeSortableValue,
  resolveDynamicColumnValue,
} from '../lib/systemTableUtils';
import { resolvePipingSystemStatus } from '../lib/exportSystemStatus';

type SortDirection = 'asc' | 'desc';

export type ExportColumnKey =
  | 'systemName'
  | 'elementCount'
  | 'sessionExportCount'
  | 'status'
  | string;

export interface IndexedPipingSystemRow {
  readonly system: PipingSystem;
  readonly values: Record<string, string>;
}

interface UseSystemTableModelOptions {
  readonly systems: PipingSystem[];
  readonly selected: Set<string>;
  readonly onSelectionChange: (selected: Set<string>) => void;
  readonly systemNameFilterValue?: string;
  readonly onSystemNameFilterChange?: (value: string) => void;
  readonly dynamicColumns: ExportColumn[];
  readonly isCompactTable: boolean;
  readonly isNarrowSystemList: boolean;
  readonly systemToolsRef?: Ref<HTMLElement>;
  readonly systemRowsRef?: Ref<HTMLElement>;
  readonly firstSystemRowRef?: Ref<HTMLElement>;
}

const STATUS_SORT_ORDER = {
  NotValidated: 0,
  Ready: 1,
  Warnings: 2,
  Errors: 3,
} satisfies Record<ExportStatus, number>;

export const ExportRowHeight = 40;
export const ExportMobileCardHeight = 78;
const ExportRowOverscan = 8;

function assignTourRef(ref: Ref<HTMLElement> | undefined, node: HTMLElement | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(node);
    return;
  }

  ref.current = node;
}

export function useSystemTableModel({
  systems,
  selected,
  onSelectionChange,
  systemNameFilterValue: controlledSystemNameFilterValue,
  onSystemNameFilterChange,
  dynamicColumns,
  isCompactTable,
  isNarrowSystemList,
  systemToolsRef,
  systemRowsRef,
  firstSystemRowRef,
}: UseSystemTableModelOptions) {
  const [sortBy, setSortBy] = useState<string>('systemName');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<ExportColumnKey, TableColumnFilter>>
  >({});
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(360);

  const setRowsRegionRef = useCallback((node: HTMLDivElement | null) => {
    tableContainerRef.current = node;
    assignTourRef(systemRowsRef, node);
  }, [systemRowsRef]);
  const setToolsRegionRef = useCallback((node: HTMLElement | null) => {
    assignTourRef(systemToolsRef, node);
  }, [systemToolsRef]);
  const setFirstRowRef = useCallback((node: HTMLElement | null) => {
    assignTourRef(firstSystemRowRef, node);
  }, [firstSystemRowRef]);

  const fixedColumnWidth = useMemo(() => ({
    selection: isCompactTable ? 40 : 44,
    systemName: isCompactTable ? 200 : 260,
    elementCount: isCompactTable ? 84 : 96,
    sessionExportCount: isCompactTable ? 84 : 96,
    dynamic: isCompactTable ? 144 : 180,
    status: isCompactTable ? 120 : 140,
    model: isCompactTable ? 56 : 64,
  }), [isCompactTable]);

  const visibleColumnKeys = useMemo<ExportColumnKey[]>(() => [
    'systemName',
    'elementCount',
    'sessionExportCount',
    ...dynamicColumns.map((column) => column.id),
    'status',
  ], [dynamicColumns]);

  const dynamicColumnById = useMemo(() => new Map(
    dynamicColumns.map((column) => [column.id, column]),
  ), [dynamicColumns]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) {
      return undefined;
    }

    const syncViewportHeight = () => {
      const rowHeight = isNarrowSystemList ? ExportMobileCardHeight : ExportRowHeight;
      setViewportHeight(Math.max(container.clientHeight || 0, rowHeight));
    };
    syncViewportHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(syncViewportHeight);
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', syncViewportHeight);
    return () => window.removeEventListener('resize', syncViewportHeight);
  }, [isNarrowSystemList]);

  useEffect(() => () => {
    if (scrollFrameRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
  }, []);

  const handleTableScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const nextScrollTop = event.currentTarget.scrollTop;
    if (typeof window.requestAnimationFrame !== 'function') {
      setScrollTop(nextScrollTop);
      return;
    }

    if (scrollFrameRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      setScrollTop(nextScrollTop);
      scrollFrameRef.current = null;
    });
  }, []);

  const getColumnValue = useCallback((system: PipingSystem, columnKey: ExportColumnKey) => {
    if (columnKey === 'systemName') {
      return String(system.systemName ?? '');
    }
    if (columnKey === 'elementCount') {
      return String(system.elementCount ?? '');
    }
    if (columnKey === 'sessionExportCount') {
      return String(system.sessionExportCount ?? 0);
    }
    if (columnKey === 'status') {
      return resolvePipingSystemStatus(system);
    }

    const dynamicColumn = dynamicColumnById.get(columnKey);
    return dynamicColumn
      ? String(resolveDynamicColumnValue(system, dynamicColumn) ?? '')
      : String(system[columnKey] ?? '');
  }, [dynamicColumnById]);

  const columnValueIndex = useMemo(() => {
    const countsByColumn = new Map<ExportColumnKey, Record<string, number>>();
    const valuesByColumn = new Map<ExportColumnKey, string[]>();
    const rows = systems.map((system): IndexedPipingSystemRow => {
      const values: Record<string, string> = {};
      visibleColumnKeys.forEach((columnKey) => {
        const value = getColumnValue(system, columnKey);
        values[columnKey] = value;
        const counts = countsByColumn.get(columnKey) ?? {};
        counts[value] = (counts[value] ?? 0) + 1;
        countsByColumn.set(columnKey, counts);
      });
      return { system, values };
    });

    visibleColumnKeys.forEach((columnKey) => {
      valuesByColumn.set(columnKey, Object.keys(countsByColumn.get(columnKey) ?? {}).sort(
        (left, right) => left.localeCompare(right, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      ));
    });
    return { countsByColumn, rows, valuesByColumn };
  }, [getColumnValue, systems, visibleColumnKeys]);

  const getUniqueValuesForColumn = useCallback((columnKey: ExportColumnKey) =>
    columnValueIndex.valuesByColumn.get(columnKey) ?? [], [columnValueIndex]);
  const getValueCountsForColumn = useCallback((columnKey: ExportColumnKey) =>
    columnValueIndex.countsByColumn.get(columnKey) ?? {}, [columnValueIndex]);

  const setColumnConditionFilter = useCallback((
    columnKey: ExportColumnKey,
    operator: TableFilterOperator,
    value: string,
  ) => {
    if (columnKey === 'systemName' && onSystemNameFilterChange) {
      if (operator === 'contains') {
        onSystemNameFilterChange(value);
        setColumnFilters((previous) => {
          if (!previous.systemName) {
            return previous;
          }
          const next = { ...previous };
          delete next.systemName;
          return next;
        });
        return;
      }

      onSystemNameFilterChange('');
    }

    setColumnFilters((previous) => {
      const requiresValue = operator !== 'isEmpty' && operator !== 'isNotEmpty';
      if (requiresValue && value.trim().length === 0) {
        const next = { ...previous };
        delete next[columnKey];
        return next;
      }
      return {
        ...previous,
        [columnKey]: { mode: 'condition', operator, value, selectedValues: null },
      };
    });
  }, [onSystemNameFilterChange]);

  const setColumnValueFilter = useCallback((
    columnKey: ExportColumnKey,
    selectedValues: string[] | null,
  ) => {
    if (columnKey === 'systemName') {
      onSystemNameFilterChange?.('');
    }
    setColumnFilters((previous) => {
      if (selectedValues === null) {
        const next = { ...previous };
        delete next[columnKey];
        return next;
      }
      return {
        ...previous,
        [columnKey]: {
          mode: 'values',
          operator: 'contains',
          value: '',
          selectedValues,
        },
      };
    });
  }, [onSystemNameFilterChange]);

  const clearColumnFilter = useCallback((columnKey: ExportColumnKey) => {
    if (columnKey === 'systemName') {
      onSystemNameFilterChange?.('');
    }
    setColumnFilters((previous) => {
      const next = { ...previous };
      delete next[columnKey];
      return next;
    });
  }, [onSystemNameFilterChange]);
  const clearAllColumnFilters = useCallback(() => {
    onSystemNameFilterChange?.('');
    setColumnFilters({});
  }, [onSystemNameFilterChange]);

  const effectiveColumnFilters = useMemo(() => {
    if (controlledSystemNameFilterValue === undefined) {
      return columnFilters;
    }

    if (controlledSystemNameFilterValue.trim().length === 0) {
      return columnFilters;
    }

    const next = { ...columnFilters };
    next.systemName = {
      mode: 'condition',
      operator: 'contains',
      value: controlledSystemNameFilterValue,
      selectedValues: null,
    };
    return next;
  }, [columnFilters, controlledSystemNameFilterValue]);

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(effectiveColumnFilters).filter(([, filter]) =>
      isFilterActive(filter),
    ) as Array<[ExportColumnKey, TableColumnFilter]>;
    const matchingRows = activeFilters.length === 0
      ? columnValueIndex.rows
      : columnValueIndex.rows.filter((row) => activeFilters.every(([columnKey, filter]) => {
        const value = row.values[columnKey] ?? '';
        return filter.mode === 'values'
          ? filter.selectedValues === null || filter.selectedValues.includes(value)
          : matchesCondition(value, filter);
      }));

    return [...matchingRows].sort((left, right) => {
      const getSortValue = (row: IndexedPipingSystemRow): number | string => {
        if (sortBy === 'status') {
          return STATUS_SORT_ORDER[row.values.status as ExportStatus]
            ?? STATUS_SORT_ORDER[resolvePipingSystemStatus(row.system)];
        }
        if (sortBy === 'elementCount') {
          return row.system.elementCount ?? 0;
        }
        if (sortBy === 'sessionExportCount') {
          return row.system.sessionExportCount ?? 0;
        }
        return normalizeSortableValue(row.values[sortBy] ?? row.system[sortBy]);
      };
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);
      const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [effectiveColumnFilters, columnValueIndex.rows, sortBy, sortDir]);

  const filtered = useMemo(() => filteredRows.map((row) => row.system), [filteredRows]);
  const filteredNameSet = useMemo(
    () => new Set(filtered.map((system) => system.systemName)),
    [filtered],
  );
  const selectedFilteredCount = useMemo(() => Array.from(selected).filter(
    (systemName) => filteredNameSet.has(systemName),
  ).length, [filteredNameSet, selected]);
  const allSelected = filtered.length > 0 && selectedFilteredCount === filtered.length;
  const someSelected = selectedFilteredCount > 0 && !allSelected;

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  }, [effectiveColumnFilters, sortBy, sortDir, systems.length]);

  const rowWindow = useMemo(() => {
    const rowHeight = isNarrowSystemList ? ExportMobileCardHeight : ExportRowHeight;
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - ExportRowOverscan);
    const visibleCount = Math.ceil(viewportHeight / rowHeight) + ExportRowOverscan * 2;
    const endIndex = Math.min(filtered.length, startIndex + visibleCount);
    return {
      bottomPadding: Math.max(0, filtered.length - endIndex) * rowHeight,
      startIndex,
      topPadding: startIndex * rowHeight,
      visibleRows: filteredRows.slice(startIndex, endIndex),
    };
  }, [filtered.length, filteredRows, isNarrowSystemList, scrollTop, viewportHeight]);

  const handleSelectAll = useCallback(() => {
    const next = new Set(selected);
    filtered.forEach((system) => allSelected
      ? next.delete(system.systemName)
      : next.add(system.systemName));
    onSelectionChange(next);
  }, [allSelected, filtered, onSelectionChange, selected]);

  const handleToggle = useCallback((systemName: string) => {
    const next = new Set(selected);
    if (next.has(systemName)) {
      next.delete(systemName);
    } else {
      next.add(systemName);
    }
    onSelectionChange(next);
  }, [onSelectionChange, selected]);

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortDir((previous) => previous === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  }, [sortBy]);
  const setSort = useCallback((column: string, direction: SortDirection) => {
    setSortBy(column);
    setSortDir(direction);
  }, []);
  const clearSort = useCallback(() => {
    setSortBy('systemName');
    setSortDir('asc');
  }, []);
  const getSortAria = useCallback((column: ExportColumnKey) => sortBy === column
    ? sortDir === 'asc' ? 'ascending' : 'descending'
    : undefined, [sortBy, sortDir]);

  const activeFilterCount = Object.values(effectiveColumnFilters).filter(isFilterActive).length;
  const systemNameFilterValue = effectiveColumnFilters.systemName?.mode === 'condition'
    && effectiveColumnFilters.systemName.operator === 'contains'
    ? effectiveColumnFilters.systemName.value
    : '';
  const showSelectionStrip = selectedFilteredCount > 0
    || activeFilterCount > 0
    || filtered.length !== systems.length;
  const tableMinWidth = Math.max(isCompactTable ? 520 : 620,
    fixedColumnWidth.selection + fixedColumnWidth.systemName
    + fixedColumnWidth.elementCount + fixedColumnWidth.sessionExportCount
    + fixedColumnWidth.status + fixedColumnWidth.model
    + dynamicColumns.length * fixedColumnWidth.dynamic);

  return {
    activeFilterCount,
    allSelected,
    clearAllColumnFilters,
    clearColumnFilter,
    clearSort,
    columnFilters: effectiveColumnFilters,
    filtered,
    fixedColumnWidth,
    getSortAria,
    getUniqueValuesForColumn,
    getValueCountsForColumn,
    handleSelectAll,
    handleSort,
    handleTableScroll,
    handleToggle,
    rowWindow,
    selectedFilteredCount,
    setColumnConditionFilter,
    setColumnValueFilter,
    setFirstRowRef,
    setRowsRegionRef,
    setSort,
    setToolsRegionRef,
    showSelectionStrip,
    someSelected,
    sortBy,
    sortDir,
    systemNameFilterValue,
    tableMinWidth,
  };
}

export type SystemTableModel = ReturnType<typeof useSystemTableModel>;
